import React from "react";
import { GasPumpIcon } from "@phosphor-icons/react";

/**
 * Compact fuel level gauge with color coding based on remaining percent.
 * Used on truck cards, fleet map popups, and truck detail.
 *
 * Props:
 *   - percentage: 0-100 (number) — live Motive fuel_primary_remaining_percentage
 *   - size: "sm" | "md" | "lg"
 *   - showLabel: also render the "%" text next to the bar
 *   - updatedAt: ISO string of when the reading was captured
 */
export default function FuelGauge({ percentage, size = "md", showLabel = true, updatedAt = null }) {
  if (percentage === null || percentage === undefined) {
    return (
      <div className="inline-flex items-center gap-1.5 text-slate-400 text-xs font-mono" data-testid="fuel-gauge-empty">
        <GasPumpIcon size={12} weight="bold" />
        <span>N/A</span>
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, Number(percentage)));
  const tone = pct < 15 ? "danger" : pct < 30 ? "warning" : "ok";
  const bg = tone === "danger" ? "bg-red-600" : tone === "warning" ? "bg-amber-500" : "bg-emerald-600";
  const border = tone === "danger" ? "border-red-300" : tone === "warning" ? "border-amber-300" : "border-emerald-300";
  const text = tone === "danger" ? "text-red-700" : tone === "warning" ? "text-amber-700" : "text-emerald-700";
  const heights = { sm: "h-1.5", md: "h-2", lg: "h-3" };
  const widths = { sm: "w-16", md: "w-24", lg: "w-32" };
  return (
    <div className="inline-flex items-center gap-2" data-testid="fuel-gauge">
      <GasPumpIcon size={size === "lg" ? 18 : 14} weight="bold" className={text} />
      <div className={`${widths[size]} bg-slate-200 rounded-sm overflow-hidden border ${border}`}>
        <div className={`${heights[size]} ${bg} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && (
        <span className={`font-mono text-xs tabular-nums font-bold ${text}`} title={updatedAt ? `Updated ${new Date(updatedAt).toLocaleString()}` : ""}>
          {pct.toFixed(0)}%
        </span>
      )}
    </div>
  );
}
